import api from './api';

export interface PartnerIncome {
  id: number;
  partnerId: number;
  projectId: number;
  amount: number;
  paymentDate: string;
  paymentMethod: 'cash' | 'cheque' | 'neft' | 'imps' | 'rtgs';
  referenceNumber?: string;
  bankName?: string;
  remarks?: string;
  receiptName?: string;
  chequeClearanceDate?: string;
  transactionType: 'income' | 'disbursement';
  status: 'approval_pending' | 'approved' | 'rejected';
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  partner: {
    id: number;
    name: string;
  };
  project: {
    id: number;
    name: string;
  };
}

export interface CreatePartnerIncomeData {
  partnerId: number;
  projectId: number;
  amount: number;
  paymentDate: string;
  paymentMethod: 'cash' | 'cheque' | 'neft' | 'imps' | 'rtgs';
  referenceNumber?: string;
  bankName?: string;
  remarks?: string;
  receiptName?: string;
  transactionType: 'income' | 'disbursement';
}

export interface PartnerIncomeFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  project?: string;
  partner?: string;
  paymentMethod?: string;
  transactionType?: string;
}

export interface PartnerIncomeResponse {
  partnerIncomes: PartnerIncome[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class PartnerIncomeService {
  // Get all partner incomes with filters and pagination
  async getPartnerIncomes(filters: PartnerIncomeFilters = {}): Promise<PartnerIncomeResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.project) params.append('project', filters.project);
    if (filters.partner) params.append('partner', filters.partner);
    if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
    if (filters.transactionType) params.append('transactionType', filters.transactionType);

    const response = await api.get(`/partner-income?${params.toString()}`);
    return response.data;
  }

  // Create new partner income
  async createPartnerIncome(data: CreatePartnerIncomeData): Promise<PartnerIncome> {
    const response = await api.post('/partner-income', data);
    return response.data;
  }

  // Update partner income
  async updatePartnerIncome(id: number, data: Partial<CreatePartnerIncomeData>): Promise<PartnerIncome> {
    const response = await api.put(`/partner-income/${id}`, data);
    return response.data;
  }

  // Delete partner income
  async deletePartnerIncome(id: number): Promise<void> {
    await api.delete(`/partner-income/${id}`);
  }

  // Get partner incomes by partner ID
  async getPartnerIncomesByPartner(partnerId: number): Promise<PartnerIncome[]> {
    const response = await api.get(`/partner-income/by-partner/${partnerId}`);
    return response.data;
  }

  // Get partners by project
  async getPartnersByProject(projectId: number): Promise<Array<{ id: number; name: string; percentage: number }>> {
    const response = await api.get(`/partners/by-project/${projectId}`);
    return response.data;
  }

  // Approve partner incomes
  async approvePartnerIncomes(incomeIds: number[]): Promise<{ message: string; approvedCount: number }> {
    const response = await api.post('/partner-income/approve', { incomeIds });
    return response.data;
  }

  // Reject partner income
  async rejectPartnerIncome(id: number, rejectionReason: string): Promise<PartnerIncome> {
    const response = await api.post(`/partner-income/${id}/reject`, { rejectionReason });
    return response.data;
  }

  // Get payment methods
  getPaymentMethods() {
    return [
      { value: 'cash', label: 'Cash' },
      { value: 'cheque', label: 'Cheque' },
      { value: 'neft', label: 'NEFT' },
      { value: 'imps', label: 'IMPS' },
      { value: 'rtgs', label: 'RTGS' }
    ];
  }

  // Get transaction types
  getTransactionTypes() {
    return [
      { value: 'income', label: 'Income (Received from Partner)' },
      { value: 'disbursement', label: 'Disbursement (Paid to Partner)' }
    ];
  }

  // Get status options
  getStatusOptions() {
    return [
      { value: 'approval_pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];
  }

  // Format amount for display
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  // Format date for display
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  // Get payment method display
  getPaymentMethodDisplay(method: string): string {
    const methods = this.getPaymentMethods();
    const found = methods.find(m => m.value === method);
    return found ? found.label : method.toUpperCase();
  }

  // Get transaction type display
  getTransactionTypeDisplay(type: string): { text: string; className: string } {
    switch (type) {
      case 'income':
        return { text: 'Income', className: 'transaction-income' };
      case 'disbursement':
        return { text: 'Disbursement', className: 'transaction-disbursement' };
      default:
        return { text: type, className: 'transaction-unknown' };
    }
  }

  // Get status display with color
  getStatusDisplay(status: string): { text: string; className: string } {
    switch (status) {
      case 'approval_pending':
        return { text: 'Pending Approval', className: 'status-pending' };
      case 'approved':
        return { text: 'Approved', className: 'status-approved' };
      case 'rejected':
        return { text: 'Rejected', className: 'status-rejected' };
      default:
        return { text: status, className: 'status-unknown' };
    }
  }
}

export const partnerIncomeService = new PartnerIncomeService();
